import React, { ReactElement, useEffect, useState } from 'react'
import PageHeader from '../components/PageHeader'
import Table from '../components/Table'
import Radom, { Payout } from '../state/Radom'
import { observer } from 'mobx-react'
import { PrimaryButton } from '../components/Button'
import { formatCurrency, useTestMode, ROUTES } from '../util/Util'
import { Container } from '../components/Animations'
import Pagination from '../components/Pagination'
import PayoutModal from './Funds/PayoutModal'
import TimeText from '../components/TimeText'
import { StatusBadge } from './CheckoutSessions'
import { Link, useNavigate } from 'react-router-dom'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import Checkbox from '../components/Checkbox'
import Dots from '../icons/Dots'
import ExportPayoutsForm from '../components/ExportPayoutsForm'
import Modal from '../components/Modal'
import { PayoutStatusFilter } from '../util/Constants'
import CopyButton from '../components/CopyButton'
import { payoutBadgeStatus, payoutStatusLabel, payoutStatusOptions } from '../util/PayoutStatus'
import {
  DropdownLabel,
  ListContent,
  ListControls,
  ListPage,
  ListPaginationRow,
  ListTableSurface,
  PageActionRow,
  PageHeaderRow,
  PageHeaderStack
} from '../components/DashboardPrimitives'

const MassivePayouts = observer((): ReactElement => {
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const pageSize = 10
  const [curPage, setCurPage] = useState(0)
  const [payouts, setPayouts] = useState<Payout[]>([])
  const [totalPayoutsSize, setTotalPayoutsSize] = useState(0)
  const [testMode] = useTestMode()

  const [selectedPayoutId, setSelectedPayoutId] = useState<string>()
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false)
  const [exportPayoutsModalOpen, setExportPayoutsModalOpen] = useState(false)

  const [payoutStatusFilter, setPayoutStatusFilter] = useState<PayoutStatusFilter[]>([

  ])

  const loadPayouts = async (): Promise<void> => {
    setIsLoading(true)
    setPayouts([])
    setTotalPayoutsSize(0)
    Radom.listPayouts(curPage * pageSize, pageSize, testMode, payoutStatusFilter, true)
      .then(res => {
        setPayouts(res.data)
        setTotalPayoutsSize(res.total)
      })
      .finally(() => setIsLoading(false)
      )
  }

  useEffect(() => {
    loadPayouts()
  }, [testMode, curPage, payoutStatusFilter])

  return <Container>
    <ListPage>
      <PageHeaderRow>
        <PageHeaderStack>
          <PageHeader title='Mass Payouts' subTitle='Manage and view your payouts' />
          <ListControls>
            <Dropdown
              selectedContent={<DropdownLabel>Payout status</DropdownLabel>}
              dropdownContent={<div>
                {payoutStatusOptions.map(s =>
                  <DropdownItem
                    key={s}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: 14 }}
                    onClick={() => {
                      if (payoutStatusFilter.includes(s)) {
                        setPayoutStatusFilter(payoutStatusFilter.filter(st => st !== s))
                      } else {
                        setPayoutStatusFilter([...payoutStatusFilter, s])
                      }
                    }}>
                    <Checkbox size={15} checked={payoutStatusFilter.includes(s)} />
                    <span>{payoutStatusLabel(s)}</span>
                  </DropdownItem>
                )}
              </div>} />
          </ListControls>
        </PageHeaderStack>
        <PageActionRow>
          <Link to={ROUTES.CREATE_MASSIVE_PAYOUT} style={{ textDecoration: 'none' }}>
            <PrimaryButton style={{ gap: 6 }}>
              <span style={{ fontSize: 12.5, lineHeight: 1, fontWeight: 500 }}>+</span>
              <span>Create mass payout</span>
            </PrimaryButton>
          </Link>
          <Dropdown
            noChevron
            selectedContent={<Dots width={15} />}
            dropdownContent={
              <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                <DropdownItem onClick={() => setExportPayoutsModalOpen(true)}>Export mass payouts</DropdownItem>
              </div>
            }
          />
        </PageActionRow>
      </PageHeaderRow>

      <ListContent>
        <ListTableSurface>
          <Table
            onClickRow={(i) => {
              if (payouts[i].status === 'Complete' || payouts[i].status === 'Failed') {
                navigate(ROUTES.getMassivePayoutDetailRoute(payouts[i].id))
                return
              }
              setSelectedPayoutId(payouts[i].id)
              setIsPayoutModalOpen(true)
            }}
            borderless
            headers={['ID', 'Status', 'Total payout', 'Gas fee', 'Completed', 'Created'].map(h => <span key={h}>{h}</span>)}
            items={
              payouts.map((p) => [
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 8 }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    minHeight: 34,
                    padding: '0 10px',
                    borderRadius: 10,
                    border: '1px solid #e7ebf2',
                    background: '#fcfcfd',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 12.5,
                    color: '#20242d'
                  }}>
                    {p.id}
                  </span>
                  <CopyButton text={p.id} />
                </div>,
                <div key={p.status}>
                  <StatusBadge status={payoutBadgeStatus(p.status)}>{payoutStatusLabel(p.status)}</StatusBadge>
                </div>,
                <div key={p.id}>{formatCurrency(p.totalAmountUsd || p.totalPayoutUsd, 'USD')}</div>,
                <div key={p.id}>{formatCurrency(p.estimatedFeeUsd, 'USD')}</div>,
                <div key={p.id}>{p.completedAt ? <TimeText date={new Date(p.completedAt)} /> : '-'}</div>,
                <div key={p.id}><TimeText date={new Date(p.createdAt)} /></div>
              ])
            }
            isLoading={isLoading}
            emptyPlaceholderText='No payouts yet'
          />
        </ListTableSurface>

        {
          !isLoading && <ListPaginationRow>
            <Pagination
              curPage={curPage}
              numPages={Math.ceil(totalPayoutsSize / pageSize)}
              onPageClick={page => setCurPage(page)}
            />
          </ListPaginationRow>
        }
      </ListContent>

    </ListPage>

    {
      exportPayoutsModalOpen &&
      <Modal visible={exportPayoutsModalOpen} onClose={() => setExportPayoutsModalOpen(false)} title='Export mass payouts'>
        <ExportPayoutsForm />
      </Modal>
    }

    {
      isPayoutModalOpen && selectedPayoutId &&
      <PayoutModal
        payoutId={selectedPayoutId}
        visible={isPayoutModalOpen}
        onPayoutConfirmed={async () => {
          await loadPayouts()
          setIsPayoutModalOpen(false)
        }}
        onClose={() => setIsPayoutModalOpen(false)} />
    }
  </Container>
})

export default MassivePayouts
